import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { UserCog, Plus, Trash2, ShieldCheck, RotateCcw, Loader2, SlidersHorizontal } from "lucide-react";
import { useDomains } from "@/context/DomainContext";
import { toast } from "sonner";

const ROLES = [
  { value: "admin", label: "Super Admin" },
  { value: "domain_admin", label: "Domain Admin" },
  { value: "auditor", label: "Read-only Auditor" },
];

export default function AdminUsers() {
  const { domains } = useDomains();
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "domain_admin", domain_ids: [] });
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ role: "domain_admin", domain_ids: [] });

  const load = async () => { try { const { data } = await api.get("/admin/portal-users"); setUsers(data); } catch (e) { toast.error(apiError(e)); setUsers([]); } };
  useEffect(() => { load(); }, []);

  const toggle = (setter) => (id) => setter((f) => ({ ...f, domain_ids: f.domain_ids.includes(id) ? f.domain_ids.filter((x) => x !== id) : [...f.domain_ids, id] }));
  const toggleCreate = toggle(setForm);
  const toggleEdit = toggle(setEditForm);

  const create = async () => {
    try { await api.post("/admin/portal-users", form); toast.success("Portal user created"); setOpen(false); setForm({ email: "", name: "", password: "", role: "domain_admin", domain_ids: [] }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveAccess = async () => {
    try { await api.patch(`/admin/portal-users/${editTarget.id}/access`, { role: editForm.role, domain_ids: editForm.domain_ids }); toast.success("Access updated"); setEditTarget(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (u) => { if (!window.confirm(`Delete ${u.email}?`)) return; try { await api.delete(`/admin/portal-users/${u.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); } };
  const reset2fa = async (u) => { try { await api.post(`/admin/portal-users/${u.id}/reset-2fa`); toast.success("2FA reset — user re-enrolls on next login"); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Portal Admins" subtitle="Team members who can access this portal" icon={UserCog}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="glossy-emerald relative rounded-xl" data-testid="create-portal-user-button"><span className="spec-shine" /><Plus className="mr-1 h-4 w-4" />Add Member</Button></DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle className="font-heading">Add portal member</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5"><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="portal-user-email" /></div>
                <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="portal-user-name" /></div>
                <div className="space-y-1.5"><Label>Temporary password</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 8 — changed at first login" data-testid="portal-user-password" /></div>
                <div className="space-y-1.5"><Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="portal-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {form.role !== "admin" && (
                  <div className="space-y-1.5"><Label>Workspaces this member can access</Label>
                    <DomainPicker domains={domains} selected={form.domain_ids} onToggle={toggleCreate} />
                  </div>
                )}
                <p className="rounded-xl bg-emerald-50 p-2 text-xs text-emerald-700">New members must change their password and enroll 2FA on first login. Super Admins get all workspaces; Auditors have read-only access to their assigned workspaces.</p>
              </div>
              <DialogFooter><Button onClick={create} className="glossy-emerald relative w-full rounded-xl" data-testid="submit-portal-user"><span className="spec-shine" />Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="glass rounded-3xl p-4">
        {users === null ? <div className="flex justify-center p-10 text-emerald-600"><Loader2 className="h-6 w-6 animate-spin" /></div> :
          users.length === 0 ? <EmptyState label="No portal members" /> :
          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between rounded-2xl bg-white/60 p-4" data-testid={`portal-user-${u.email}`}>
                <div>
                  <p className="font-medium text-slate-800">{u.name}</p>
                  <p className="text-xs text-slate-500">{u.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-100 text-emerald-700">{ROLES.find((r) => r.value === u.role)?.label || u.role}</Badge>
                  {u.role === "admin" ? <Badge className="bg-amber-50 text-amber-700">All domains</Badge> : <Badge className="bg-slate-100 text-slate-600">{(u.domain_ids || []).length} domain(s)</Badge>}
                  {u.totp_enabled ? <Badge className="bg-teal-50 text-teal-700"><ShieldCheck className="mr-1 h-3 w-3" />2FA</Badge> : <Badge className="bg-slate-100 text-slate-500">No 2FA</Badge>}
                  <Button size="sm" variant="ghost" className="h-8 text-slate-500" onClick={() => { setEditTarget(u); setEditForm({ role: u.role, domain_ids: u.domain_ids || [] }); }} title="Edit access" data-testid={`edit-access-${u.email}`}><SlidersHorizontal className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" className="h-8 text-slate-500" onClick={() => reset2fa(u)} title="Reset 2FA" data-testid={`reset-2fa-${u.email}`}><RotateCcw className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" className="h-8 text-rose-500" onClick={() => remove(u)} data-testid={`delete-portal-user-${u.email}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>}
      </div>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Edit access</DialogTitle></DialogHeader>
          <p className="-mt-2 text-sm text-slate-500">{editTarget?.email}</p>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Role</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger data-testid="edit-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {editForm.role !== "admin" && (
              <div className="space-y-1.5"><Label>Accessible workspaces</Label>
                <DomainPicker domains={domains} selected={editForm.domain_ids} onToggle={toggleEdit} />
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={saveAccess} className="glossy-emerald relative w-full rounded-xl" data-testid="save-access"><span className="spec-shine" />Save access</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DomainPicker({ domains, selected, onToggle }) {
  if (!domains || domains.length === 0) return <p className="text-xs text-slate-400">No workspaces connected yet.</p>;
  return (
    <div className="flex flex-wrap gap-2" data-testid="domain-picker">
      {domains.map((d) => {
        const on = selected.includes(d.id);
        return (
          <button type="button" key={d.id} onClick={() => onToggle(d.id)} data-testid={`pick-domain-${d.domain}`}
            className={`relative rounded-full px-3 py-1 text-xs font-medium transition ${on ? "glossy-emerald" : "border border-slate-200 bg-white/70 text-slate-600"}`}>
            {on && <span className="spec-shine" />}{d.label}
          </button>
        );
      })}
    </div>
  );
}
